import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Scan, Cpu, FileText, Wrench, Upload, ArrowRight, ArrowLeft,
  RefreshCw, AlertTriangle, Check, ChevronDown, Info, History
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { VideoRecorder } from '../components/features/VideoRecorder';
import { AnalysisResult } from '../components/features/AnalysisResult';
import { RepairWizard } from '../components/features/RepairWizard';
import { SafetyGate } from '../components/features/SafetyGate';
import { api, abortAllRequests } from '../lib/api';
import { saveRepairLog } from '../lib/repairLogs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const STEPS = {
  UPLOAD: 'upload',
  ANALYZING: 'analyzing',
  DEVICE_IDENTIFIED: 'device_identified',
  MANUAL_OPTION: 'manual_option',
  DIAGNOSING: 'diagnosing',
  RESULTS: 'results',
  REPAIR: 'repair',
  COMPLETE: 'complete',
};

export default function HomePage() {
  const activeFlowRef = React.useRef(0);
  const [currentStep, setCurrentStep] = useState(STEPS.UPLOAD);
  const [sessionId, setSessionId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [diagnosis, setDiagnosis] = useState(null);
  const [safetyCheck, setSafetyCheck] = useState(null);
  const [repairSteps, setRepairSteps] = useState([]);
  const [showSafetyGate, setShowSafetyGate] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [riskAcknowledged, setRiskAcknowledged] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleVideoCapture = useCallback(async (file) => {
    // Start new flow
    const flowId = activeFlowRef.current + 1;
    activeFlowRef.current = flowId;

    try {
      setIsProcessing(true);
      setProcessingMessage('Creating session...');

      // Create session
      const session = await api.createSession();
      setSessionId(session.id);

      if (activeFlowRef.current !== flowId) return;

      // Upload video
      setProcessingMessage('Uploading video...');
      await api.uploadVideo(session.id, file);

      if (activeFlowRef.current !== flowId) return;

      // Analyze device
      setCurrentStep(STEPS.ANALYZING);
      setProcessingMessage('Gemini 3 AI is analyzing your device...');
      const deviceResult = await api.analyzeDevice(session.id);

      if (activeFlowRef.current !== flowId) return;
      setDeviceInfo(deviceResult);

      setCurrentStep(STEPS.DEVICE_IDENTIFIED);
      toast.success('Device identified successfully!');

    } catch (error) {
      console.error('Error processing video:', error);
      toast.error(error.response?.data?.detail || 'Failed to process video');
      setCurrentStep(STEPS.UPLOAD);
    } finally {
      setIsProcessing(false);
      setProcessingMessage('');
    }
  }, []);

  const handleFileUpload = handleVideoCapture;

  const handleManualUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !sessionId) return;

    // Continue current flow but verify it's still active
    const flowId = activeFlowRef.current;

    try {
      setIsProcessing(true);
      setProcessingMessage('Uploading manual...');
      await api.uploadManual(sessionId, file);

      if (activeFlowRef.current !== flowId) return;

      toast.success('Manual uploaded successfully!');
      await runDiagnosis(flowId);
    } catch (error) {
      console.error('Error uploading manual:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload manual');
      setIsProcessing(false);
    }
  };

  const handleSkipManual = async () => {
    await runDiagnosis(activeFlowRef.current);
  };

  const runDiagnosis = async (flowId = activeFlowRef.current) => {
    if (!sessionId) return;

    try {
      setCurrentStep(STEPS.DIAGNOSING);
      setIsProcessing(true);
      setProcessingMessage('Gemini 3 running expert diagnosis...');

      // Run diagnosis
      const diagnosisResult = await api.diagnose(sessionId);

      if (activeFlowRef.current !== flowId) return;
      setDiagnosis(diagnosisResult);

      // Run safety check
      setProcessingMessage('Gemini 3 evaluating safety...');
      const safetyResult = await api.safetyCheck(sessionId);

      if (activeFlowRef.current !== flowId) return;
      setSafetyCheck(safetyResult);

      setCurrentStep(STEPS.RESULTS);
      toast.success('Diagnosis complete!');

    } catch (error) {
      console.error('Error during diagnosis:', error);
      toast.error(error.response?.data?.detail || 'Diagnosis failed');
    } finally {
      setIsProcessing(false);
      setProcessingMessage('');
    }
  };

  const handleProceedToRepair = () => {
    // Always show safety gate modal - it handles both safe and unsafe repair flows
    setShowSafetyGate(true);
  };

  const handleSafetyConfirm = async () => {
    setShowSafetyGate(false);

    if (!sessionId) return;

    try {
      setIsProcessing(true);
      setProcessingMessage('Gemini 3 generating repair steps...');

      // Pass forceProceed=true if repair was not allowed (user already acknowledged via SafetyGate checkbox)
      const forceProceed = !safetyCheck?.repair_allowed;

      // Capture current flow ID
      const flowId = activeFlowRef.current;

      const stepsResult = await api.generateRepairSteps(sessionId, forceProceed);

      if (activeFlowRef.current !== flowId) return;

      if (stepsResult.error) {
        toast.error(stepsResult.error);
        return;
      }

      setRepairSteps(stepsResult.steps || []);
      setCurrentStep(STEPS.REPAIR);
      toast.success('Repair steps ready!');

    } catch (error) {
      console.error('Error generating repair steps:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate repair steps');
    } finally {
      setIsProcessing(false);
      setProcessingMessage('');
    }
  };

  const handleReset = () => {
    // Abort all in-flight API requests
    abortAllRequests();

    // Invalidate any active flows
    activeFlowRef.current += 1;

    setCurrentStep(STEPS.UPLOAD);
    setSessionId(null);
    setDeviceInfo(null);
    setDiagnosis(null);
    setSafetyCheck(null);
    setRepairSteps([]);
    setIsProcessing(false);
    setProcessingMessage('');
  };

  const handleHomeClick = () => {
    // Always reset the state first
    handleReset();
    // Only navigate if we're not already on the home page
    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  const handleRepairComplete = () => {
    // Save repair log
    try {
      saveRepairLog({
        deviceInfo,
        diagnosis,
        safetyCheck,
        repairSteps,
      });
    } catch (error) {
      console.error('Error saving repair log:', error);
    }

    setCurrentStep(STEPS.COMPLETE);
    toast.success('Congratulations! Repair completed!');
  };

  return (
    <div className="min-h-screen bg-background grid-pattern flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-surface/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleHomeClick}
            >
              <div className="w-10 h-10 bg-primary flex items-center justify-center">
                {currentStep === STEPS.UPLOAD ? (
                  <Wrench className="w-5 h-5 text-white" />
                ) : (
                  <ArrowLeft className="w-5 h-5 text-white" />
                )}
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold uppercase tracking-tight text-white">
                  FIXIT.AI
                </h1>
                <span className="font-mono text-xs text-text-muted hidden sm:inline-block">
                  Gemini 3 • Point. Scan. Fix.
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {sessionId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleHomeClick}
                  className="border-border hover:border-primary"
                  data-testid="new-session-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  New Session
                </Button>
              )}

              <Button
                variant="ghost"
                className="text-xl text-white hover:text-primary font-heading font-bold uppercase tracking-tight"
                onClick={() => navigate('/logs')}
              >
                <History className="w-5 h-5 sm:mr-2" />
                <span className="hidden sm:inline">History</span>
              </Button>

              <Button
                variant="ghost"
                className="text-xl text-white hover:text-primary font-heading font-bold uppercase tracking-tight"
                onClick={() => navigate('/about')}
              >
                <Info className="w-5 h-5 sm:mr-2" />
                <span className="hidden sm:inline">Inside FixIt.AI</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Progress indicator */}
      {currentStep !== STEPS.UPLOAD && (
        <div className="bg-surface border-b border-border">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-1">
              {[
                { key: 'upload', label: 'Upload', icon: Upload },
                { key: 'analyze', label: 'Analyze', icon: Scan },
                { key: 'diagnose', label: 'Diagnose', icon: Cpu },
                { key: 'repair', label: 'Repair', icon: Wrench },
              ].map((step, index) => {
                const isActive =
                  (step.key === 'upload' && currentStep === STEPS.UPLOAD) ||
                  (step.key === 'analyze' && [STEPS.ANALYZING, STEPS.DEVICE_IDENTIFIED, STEPS.MANUAL_OPTION].includes(currentStep)) ||
                  (step.key === 'diagnose' && [STEPS.DIAGNOSING, STEPS.RESULTS].includes(currentStep)) ||
                  (step.key === 'repair' && [STEPS.REPAIR, STEPS.COMPLETE].includes(currentStep));

                const isPast =
                  (step.key === 'upload') ||
                  (step.key === 'analyze' && [STEPS.DIAGNOSING, STEPS.RESULTS, STEPS.REPAIR, STEPS.COMPLETE].includes(currentStep)) ||
                  (step.key === 'diagnose' && [STEPS.REPAIR, STEPS.COMPLETE].includes(currentStep));

                return (
                  <React.Fragment key={step.key}>
                    <div className={cn(
                      "flex items-center gap-2 px-3 py-1.5 whitespace-nowrap",
                      isActive ? "text-primary" : isPast ? "text-success" : "text-text-muted"
                    )}>
                      {isPast && !isActive ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <step.icon className="w-4 h-4" />
                      )}
                      <span className="font-mono text-xs uppercase">{step.label}</span>
                    </div>
                    {index < 3 && (
                      <ArrowRight className="w-4 h-4 text-text-muted flex-shrink-0" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8 sm:py-12 flex-grow">
        <AnimatePresence mode="wait">
          {/* Upload step */}
          {currentStep === STEPS.UPLOAD && (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Hero section */}
              <div className="text-center mb-8 sm:mb-12 px-2">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 mb-6"
                >
                  <Scan className="w-4 h-4 text-primary" />
                  <span className="font-mono text-sm text-primary">AI-POWERED REPAIR</span>
                </motion.div>

                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-4 leading-tight">
                  Point your camera at<br />
                  <span className="text-primary">anything broken</span>
                </h1>
                <p className="text-text-secondary text-base sm:text-lg max-w-2xl mx-auto px-2">
                  If a human technician can diagnose it, Gemini 3 can guide you.
                  Record a video of your broken device and let AI do the rest.
                </p>
              </div>

              <VideoRecorder
                onVideoCapture={handleVideoCapture}
                onFileUpload={handleFileUpload}
                isProcessing={isProcessing}
              />
            </motion.div>
          )}

          {/* Analyzing step */}
          {currentStep === STEPS.ANALYZING && (
            <motion.div
              key="analyzing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="relative w-32 h-32 mb-8">
                <div className="absolute inset-0 border-2 border-secondary/30 animate-pulse" />
                <div className="absolute inset-2 border border-secondary/50" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Scan className="w-12 h-12 text-secondary animate-pulse" />
                </div>
                <div className="scanning-beam" />
              </div>

              <h2 className="font-heading text-2xl font-bold uppercase text-white mb-2">
                ANALYZING
              </h2>
              <p className="text-text-secondary font-mono text-sm" data-testid="processing-message">
                {processingMessage || 'Processing...'}
              </p>
            </motion.div>
          )}

          {/* Device identified - Manual option */}
          {currentStep === STEPS.DEVICE_IDENTIFIED && (
            <motion.div
              key="device-identified"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="max-w-2xl mx-auto space-y-8"
            >
              {/* Device info summary */}
              <div className="bg-surface border border-success/30 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Check className="w-6 h-6 text-success" />
                  <span className="font-heading text-lg uppercase text-success">
                    Device Identified
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <span className="font-mono text-xs text-text-muted block">TYPE</span>
                    <span className="text-white text-sm sm:text-base">{deviceInfo?.device_type || 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="font-mono text-xs text-text-muted block">BRAND</span>
                    <span className="text-white text-sm sm:text-base">{deviceInfo?.brand || 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="font-mono text-xs text-text-muted block">MODEL</span>
                    <span className="text-white font-mono text-sm sm:text-base">{deviceInfo?.model || 'Unknown'}</span>
                  </div>
                </div>
              </div>

              {/* Manual upload option */}
              <div className="text-center space-y-6">
                <div>
                  <h2 className="font-heading text-xl font-bold uppercase text-white mb-2">
                    ENHANCE DIAGNOSIS
                  </h2>
                  <p className="text-text-secondary text-sm">
                    Upload the service manual for more accurate repair guidance
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleManualUpload}
                      className="hidden"
                      data-testid="manual-upload-input"
                    />
                    <div className="flex items-center gap-2 px-6 py-3 bg-secondary text-black 
                                  font-heading uppercase hover:bg-secondary/90 transition-colors w-full sm:w-auto justify-center">
                      <FileText className="w-5 h-5" />
                      Upload Manual (PDF)
                    </div>
                  </label>

                  <span className="text-text-muted">or</span>

                  <Button
                    onClick={handleSkipManual}
                    variant="outline"
                    className="border-border hover:border-primary"
                    data-testid="skip-manual-btn"
                  >
                    Continue without manual
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Diagnosing step */}
          {currentStep === STEPS.DIAGNOSING && (
            <motion.div
              key="diagnosing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="relative w-32 h-32 mb-8">
                <div className="absolute inset-0 border-2 border-primary/30" />
                <div className="absolute inset-2 border border-primary/50 animate-pulse" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Cpu className="w-12 h-12 text-primary animate-pulse" />
                </div>
              </div>

              <h2 className="font-heading text-2xl font-bold uppercase text-white mb-2">
                EXPERT DIAGNOSIS
              </h2>
              <p className="text-text-secondary font-mono text-sm">
                {processingMessage || 'AI is reasoning through the problem...'}
              </p>
            </motion.div>
          )}

          {/* Results step */}
          {currentStep === STEPS.RESULTS && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              <AnalysisResult
                deviceInfo={deviceInfo}
                diagnosis={diagnosis}
                safetyCheck={safetyCheck}
              />

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-2 sm:px-0">
                <Button
                  onClick={handleProceedToRepair}
                  className={`w-full sm:w-auto font-heading uppercase px-8 py-5 sm:py-6 text-base sm:text-lg ${safetyCheck?.repair_allowed
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-warning text-black hover:bg-warning/90'
                    }`}
                  disabled={isProcessing}
                  data-testid="proceed-repair-btn"
                >
                  {isProcessing ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Generating Steps...
                    </>
                  ) : (
                    <>
                      <Wrench className="w-5 h-5 mr-2" />
                      {safetyCheck?.repair_allowed ? 'Start Repair Guide' : 'Generate Repair Steps'}
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Repair wizard step */}
          {currentStep === STEPS.REPAIR && (
            <motion.div
              key="repair"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <RepairWizard
                steps={repairSteps}
                onComplete={handleRepairComplete}
                onReset={handleReset}
                safetyRisk={diagnosis?.safety_risk}
                deviceType={deviceInfo?.device_type?.toLowerCase() || 'electrical'}
                sessionId={sessionId}
              />
            </motion.div>
          )}

          {/* Complete step */}
          {currentStep === STEPS.COMPLETE && (
            <motion.div
              key="complete"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="flex flex-col items-center justify-center py-20 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', delay: 0.2 }}
                className="w-24 h-24 bg-success flex items-center justify-center mb-8"
              >
                <Check className="w-12 h-12 text-black" />
              </motion.div>

              <h2 className="font-heading text-3xl font-bold uppercase text-white mb-4">
                REPAIR COMPLETE!
              </h2>
              <p className="text-text-secondary mb-8 max-w-md">
                Congratulations! You've successfully completed the repair.
                Your device should now be working properly.
              </p>

              <Button
                onClick={handleHomeClick}
                className="bg-primary text-white hover:bg-primary/90 font-heading uppercase"
                data-testid="start-new-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Fix Another Device
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Safety gate modal */}
      <SafetyGate
        isOpen={showSafetyGate}
        onClose={() => setShowSafetyGate(false)}
        onConfirm={handleSafetyConfirm}
        safetyNotice={safetyCheck?.safety_notice || (safetyCheck?.repair_allowed
          ? 'Review safety considerations before proceeding with this repair.'
          : 'Based on our safety analysis, this repair should be performed by a professional technician. Proceeding may result in injury or further damage.'
        )}
        mode={safetyCheck?.repair_allowed ? safetyCheck?.mode : 'full'}
        risk={diagnosis?.safety_risk}
      />

      {/* Footer */}
      <footer className="border-t border-border bg-surface/50 mt-auto">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="font-mono text-xs text-text-muted">
              FIXIT.AI © 2025 — AI-Powered Repair Assistant
            </span>
            <div className="flex items-center gap-4 text-text-muted text-xs">
              <span>Powered by Gemini 3</span>
              <span>•</span>
              <span>Safety First</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
